import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../../environments/environment';
import { AssetService } from '../asset/asset.service';

declare var PubNub: any;

export interface ChatModerationContext {
  assetId: string;
  channel: string;
  chatEnabled: boolean;
  enablePrivateRooms: boolean;
  roomPrefix: string;
  configLoaded: boolean;
}

export interface ChatModMessage {
  timetoken: string;
  userId: string;
  username: string;
  text: string;
  createdAt: Date;
  role: string;
  deleted: boolean;
  raw: any;
}

@Injectable({
  providedIn: 'root'
})
export class ChatModerationService implements OnDestroy {
  private pubnub: any = null;
  private currentChannel = '';
  private currentAssetId = '';
  private modApiUrl = ((environment as any).modApiUrl || (environment.apiURL + environment.adminApiUrl + '/mod'));
  private deletedTimetokens: {[key: string]: boolean} = {};

  private emptyContext: ChatModerationContext = {
    assetId: '',
    channel: '',
    chatEnabled: false,
    enablePrivateRooms: false,
    roomPrefix: '',
    configLoaded: false
  };

  context$ = new BehaviorSubject<ChatModerationContext>(this.emptyContext);
  messages$ = new BehaviorSubject<ChatModMessage[]>([]);
  pinnedMessage$ = new BehaviorSubject<ChatModMessage | null>(null);
  connectionState$ = new BehaviorSubject<string>('offline');
  presenceCount$ = new BehaviorSubject<number>(0);
  channelState$ = new BehaviorSubject<any>({});
  activePoll$ = new BehaviorSubject<any>(null);
  loading$ = new BehaviorSubject<boolean>(false);

  constructor(
    private http: HttpClient,
    private assetService: AssetService,
    private toastrService: ToastrService
  ) {}

  ngOnDestroy() {
    this.disconnect();
  }

  loadForAsset(assetId: string) {
    this.currentAssetId = assetId || '';
    this.loading$.next(true);
    this.disconnect();
    this.messages$.next([]);
    this.pinnedMessage$.next(null);
    this.presenceCount$.next(0);
    this.channelState$.next({});
    this.activePoll$.next(null);
    this.deletedTimetokens = {};

    this.assetService.getConfig(assetId).subscribe(
      (response: any) => {
        const raw = this.parseConfig(response);
        const context = this.resolveContext(assetId, raw);
        this.context$.next(context);
        this.loading$.next(false);

        if (!context.channel) {
          this.connectionState$.next('offline');
          return;
        }

        this.loadRecentMessages(context.assetId, context.channel);
        this.loadChannelState(context.assetId, context.channel);
        this.connect(context.channel);
      },
      (_err: any) => {
        this.loading$.next(false);
        this.context$.next({
          assetId,
          channel: '',
          chatEnabled: false,
          enablePrivateRooms: false,
          roomPrefix: '',
          configLoaded: true
        });
        this.connectionState$.next('offline');
        this.toastrService.warning('Unable to load stream config for chat moderation.');
      }
    );
  }

  postAnnouncement(text: string, pin: boolean): Observable<any> {
    return this.post('announce', { message: text, pin: !!pin });
  }

  pinMessage(message: ChatModMessage): Observable<any> {
    const rawMessage = this.rawMessagePayload(message.raw || message);
    const username = this.usernameForPinnedMessage(message);
    const pinPayload = Object.assign({}, rawMessage, {
      username,
      userId: rawMessage.userId || message.userId,
      text: rawMessage.text || message.text,
      role: rawMessage.role || message.role
    });

    return this.post('pin', { timetoken: message.timetoken, message: pinPayload });
  }

  usernameForPinnedMessage(message: ChatModMessage): string {
    const envelope = message && message.raw ? message.raw : (message || {});
    const raw = this.rawMessagePayload(envelope);
    const meta = raw.meta || envelope.meta || {};
    const user = raw.user || raw.author || {};

    return (
      raw.username ||
      raw.userName ||
      user.name ||
      meta.name ||
      envelope.username ||
      envelope.userName ||
      message.username ||
      message.userId ||
      'unknown'
    ).toString();
  }

  unpinMessage(): Observable<any> {
    return this.post('unpin', {});
  }

  deleteMessage(message: ChatModMessage): Observable<any> {
    return this.post('delete-message', { timetoken: message.timetoken, userId: message.userId });
  }

  banUser(userId: string, duration: string, reason: string): Observable<any> {
    return this.post('ban-user', { userId, duration, reason });
  }

  unbanUser(userId: string): Observable<any> {
    return this.post('unban-user', { userId });
  }

  saveFilters(words: string[], slowModeEnabled: boolean, slowModeSeconds: number): Observable<any> {
    return this.post('filters', {
      filterWords: words || [],
      slowMode: {
        enabled: !!slowModeEnabled,
        seconds: slowModeSeconds || 0
      }
    });
  }

  launchPoll(question: string, options: string[]): Observable<any> {
    return this.post('poll/launch', { question, options });
  }

  closePoll(): Observable<any> {
    return this.post('poll/close', {});
  }

  pollResults(): Observable<any> {
    const ctx = this.context$.getValue();
    const url = this.modApiUrl + '/poll/results?assetId=' + encodeURIComponent(ctx.assetId) + '&channel=' + encodeURIComponent(ctx.channel);
    return this.http.get(url);
  }

  markMessageDeleted(timetoken: string) {
    if (timetoken) this.deletedTimetokens[String(timetoken)] = true;
    const list = this.messages$.getValue().slice();
    for (let i = 0; i < list.length; i++) {
      if (String(list[i].timetoken) === String(timetoken)) {
        list[i] = Object.assign({}, list[i], { deleted: true });
      }
    }
    this.messages$.next(list);
  }

  private parseConfig(response: any): any {
    if (!response || !response.data || !response.data.config_data) return null;
    try {
      return JSON.parse(response.data.config_data);
    } catch (_err) {
      return null;
    }
  }

  private resolveContext(assetId: string, raw: any): ChatModerationContext {
    const chatConfig = raw && raw.chatConfig ? raw.chatConfig : {};
    const channel = (chatConfig.channel || (raw && raw.chatChannel) || (raw && raw.signalChannel) || '').toString().trim();
    const enabled = !!(channel && (chatConfig.showChat || (raw && raw.chatEnabled) || chatConfig.channel || (raw && raw.chatChannel)));
    const privateEnabled = !!(chatConfig.enablePrivateRooms || (raw && raw.chatEnablePrivate));
    const explicitPrefix = (chatConfig.roomPrefix || (raw && raw.chatRoomPrefix) || '').toString().trim();
    const roomPrefix = privateEnabled ? (explicitPrefix || (channel ? channel + '-private-room' : '')) : '';

    return {
      assetId,
      channel,
      chatEnabled: enabled,
      enablePrivateRooms: privateEnabled,
      roomPrefix,
      configLoaded: true
    };
  }

  private connect(channel: string) {
    if (!channel) return;
    if (typeof PubNub === 'undefined') {
      this.connectionState$.next('offline');
      this.toastrService.warning('PubNub SDK is not available on this page.');
      return;
    }

    this.currentChannel = channel;
    this.connectionState$.next('reconnecting');

    this.pubnub = new PubNub({
      subscribeKey: 'sub-c-15859c61-2a14-48e3-adbb-81b39c3e53e0',
      uuid: this.getModeratorId()
    });

    const self = this;
    this.pubnub.addListener({
      status: function(status: any) {
        const category = status && status.category ? status.category : '';
        if (category === 'PNConnectedCategory') self.connectionState$.next('connected');
        else if (category === 'PNReconnectedCategory') self.connectionState$.next('connected');
        else if (category === 'PNNetworkDownCategory') self.connectionState$.next('offline');
        else if (category === 'PNNetworkUpCategory') self.connectionState$.next('reconnecting');
      },
      message: function(event: any) {
        self.addMessage(self.normalizePubnubMessage(event));
      },
      presence: function(event: any) {
        if (event && typeof event.occupancy === 'number') {
          self.presenceCount$.next(event.occupancy);
        }
      },
      messageAction: function(event: any) {
        const data = event && event.data ? event.data : null;
        if (data && data.type === 'deleted') self.markMessageDeleted(data.messageTimetoken);
      },
      objects: function(event: any) {
        self.applyChannelObjectEvent(event);
      }
    });

    this.pubnub.subscribe({ channels: [channel], withPresence: true });
  }

  private disconnect() {
    if (!this.pubnub) return;
    try {
      if (this.currentChannel) this.pubnub.unsubscribe({ channels: [this.currentChannel] });
      if (this.pubnub.removeAllListeners) this.pubnub.removeAllListeners();
    } catch (_err) {}
    this.pubnub = null;
    this.currentChannel = '';
    this.connectionState$.next('offline');
  }

  private loadRecentMessages(assetId: string, channel: string) {
    const url = this.modApiUrl + '/recent?assetId=' + encodeURIComponent(assetId) + '&channel=' + encodeURIComponent(channel);
    this.http.get(url).subscribe(
      (res: any) => {
        const items = res && res.data ? res.data : (res && res.messages ? res.messages : []);
        if (!Array.isArray(items)) return;
        const messages: ChatModMessage[] = [];
        for (let i = 0; i < items.length; i++) messages.push(this.normalizeApiMessage(items[i]));
        this.messages$.next(messages);
      },
      (_err: any) => {}
    );
  }

  private loadChannelState(assetId: string, channel: string) {
    const url = this.modApiUrl + '/state?assetId=' + encodeURIComponent(assetId) + '&channel=' + encodeURIComponent(channel);
    this.http.get(url).subscribe(
      (res: any) => {
        const state = res && res.data ? res.data : (res || {});
        this.channelState$.next(state);
        if (state && state.pinnedMessage) this.pinnedMessage$.next(this.normalizePinnedApiMessage(state.pinnedMessage));
        if (state && state.activePoll) this.activePoll$.next(state.activePoll);
      },
      (_err: any) => {}
    );
  }

  private post(path: string, body: any): Observable<any> {
    const ctx = this.context$.getValue();
    const payload = Object.assign({}, body || {}, {
      assetId: ctx.assetId || this.currentAssetId,
      channel: ctx.channel,
      moderator: this.getModerator()
    });
    return this.http.post(this.modApiUrl + '/' + path, payload);
  }

  private addMessage(message: ChatModMessage) {
    if (!message || !message.timetoken) return;
    if (this.deletedTimetokens[String(message.timetoken)]) message.deleted = true;
    const list = this.messages$.getValue().slice();
    for (let i = 0; i < list.length; i++) {
      if (String(list[i].timetoken) === String(message.timetoken)) {
        if (list[i].deleted) message.deleted = true;
        list[i] = message;
        this.messages$.next(list);
        return;
      }
    }
    list.push(message);
    list.sort(function(a: ChatModMessage, b: ChatModMessage) {
      return Number(a.timetoken || 0) - Number(b.timetoken || 0);
    });
    this.messages$.next(list);
  }

  private normalizePubnubMessage(event: any): ChatModMessage {
    const raw = event && event.message ? event.message : {};
    const meta = raw.meta || event.userMetadata || {};
    const user = raw.user || raw.author || {};
    const userId = (raw.userId || user.id || meta.userId || event.publisher || 'unknown').toString();
    const text = (raw.text || raw.message || raw.body || '').toString();
    const timetoken = (event && event.timetoken ? event.timetoken : raw.timetoken || Date.now()).toString();
    const role = (meta.role || raw.role || '').toString();
    const deleted = !!raw.deleted || !!this.deletedTimetokens[timetoken];
    const username = this.displayNameForMessage(raw, user, meta, userId, role);
    return {
      timetoken,
      userId,
      username,
      text,
      createdAt: this.dateFromTimetoken(timetoken),
      role,
      deleted,
      raw
    };
  }

  private normalizeApiMessage(raw: any): ChatModMessage {
    raw = raw || {};
    const message = this.unwrapApiMessage(raw);
    const meta = message.meta || raw.meta || {};
    const user = message.user || message.author || raw.user || raw.author || {};
    const userId = (message.userId || user.id || meta.userId || raw.publisher || 'unknown').toString();
    const text = (message.text || (typeof message.message === 'string' ? message.message : '') || message.body || '').toString();
    const timetoken = (message.timetoken || message.messageTimetoken || message.id || Date.now()).toString();
    const role = (meta.role || message.role || '').toString();
    const deleted = !!message.deleted || !!this.deletedTimetokens[timetoken];
    const username = this.displayNameForMessage(message, user, meta, userId, role);
    return {
      timetoken,
      userId,
      username,
      text,
      createdAt: raw.createdAt ? new Date(raw.createdAt) : this.dateFromTimetoken(timetoken),
      role,
      deleted,
      raw
    };
  }

  private normalizePinnedApiMessage(raw: any): ChatModMessage {
    const message = this.normalizeApiMessage(raw);
    return Object.assign({}, message, {
      username: this.usernameForPinnedMessage(message)
    });
  }

  private unwrapApiMessage(raw: any): any {
    const nested = this.rawMessagePayload(raw);
    return nested !== raw ? Object.assign({}, raw, nested) : raw;
  }

  private rawMessagePayload(raw: any): any {
    if (raw && raw.raw && typeof raw.raw === 'object') return raw.raw;
    if (raw && raw.message && typeof raw.message === 'object') return raw.message;
    if (raw && raw.entry && typeof raw.entry === 'object') return raw.entry;
    return raw || {};
  }

  private dateFromTimetoken(timetoken: string): Date {
    const n = Number(timetoken);
    if (!isNaN(n) && n > 1000000000000000) return new Date(Math.floor(n / 10000));
    if (!isNaN(n) && n > 1000000000) return new Date(n);
    return new Date();
  }

  private applyChannelObjectEvent(event: any) {
    if (!event) return;

    const message = event.message || event;
    const data = event.data || message.data || {};
    const custom = data.custom || message.custom || event.custom || {};
    const channel = custom.channel || data.id || data.channel || message.id || event.channel;
    if (channel && this.currentChannel && String(channel) !== String(this.currentChannel)) return;

    const state = message.channelState || data.channelState || event.channelState || this.stateFromCustom(custom);
    if (!state) return;

    this.channelState$.next(state);
    if (state.hasOwnProperty('pinnedMessage')) {
      this.pinnedMessage$.next(state.pinnedMessage ? this.normalizePinnedApiMessage(state.pinnedMessage) : null);
    }
    if (state.hasOwnProperty('activePoll')) {
      this.activePoll$.next(state.activePoll || null);
    }
  }

  private stateFromCustom(custom: any): any {
    if (!custom || typeof custom !== 'object') return null;
    const hasState = custom.hasOwnProperty('pinnedMessage') ||
      custom.hasOwnProperty('activePoll') ||
      custom.hasOwnProperty('bans') ||
      custom.hasOwnProperty('filterWords') ||
      custom.hasOwnProperty('slowMode') ||
      custom.hasOwnProperty('slowModeEnabled') ||
      custom.hasOwnProperty('slowModeSeconds');
    if (!hasState) return null;

    return {
      pinnedMessage: this.parseStateValue(custom.pinnedMessage, null),
      activePoll: this.parseStateValue(custom.activePoll, null),
      bans: this.parseStateValue(custom.bans, []),
      filterWords: this.parseStateValue(custom.filterWords, []),
      slowMode: this.parseStateValue(custom.slowMode, {
        enabled: !!custom.slowModeEnabled,
        seconds: Number(custom.slowModeSeconds || 0) || 0
      })
    };
  }

  private parseStateValue(value: any, fallback: any): any {
    if (value === undefined || value === null || value === '') return fallback;
    if (typeof value !== 'string') return value;
    try {
      return JSON.parse(value);
    } catch (_err) {
      return fallback;
    }
  }

  private displayNameForMessage(raw: any, user: any, meta: any, userId: string, role: string): string {
    const isModerator = ['moderator', 'mod', 'admin'].indexOf((role || '').toLowerCase()) !== -1;
    if (isModerator) {
      return (raw.username || raw.userName || user.name || meta.name || userId).toString();
    }

    return (raw.displayName || raw.userDisplayName || raw.userName || raw.username || user.name || meta.displayName || meta.name || userId).toString();
  }

  private getModeratorId(): string {
    return (localStorage.getItem('ud') || localStorage.getItem('email') || 'Volta_mod').toString();
  }

  private getModerator(): any {
    return {
      id: this.getModeratorId(),
      name: localStorage.getItem('email') || this.getModeratorId(),
      role: localStorage.getItem('roleName') || 'moderator'
    };
  }
}
